import styles from './DocumentPreviewCard.module.css'

import React from 'react'
import { useTranslation } from 'react-i18next'

import { classNames } from 'primereact/utils'

import {
  AttachmentDownloadButton,
  AttachmentOpenInTabButton,
} from '../../../../components/attachments'
import { EInvoiceAttachment } from '../../../../data/models'
import { getFormattedFileSize } from '../../../../utils/formatUtils'
import { VirusCheckStatus } from '../virus-check-status'

type Props = {
  attachment: EInvoiceAttachment
  fullWidth?: boolean
}

export const DocumentPreviewCard: React.FC<Props> = ({
  attachment,
  fullWidth = false,
}) => {
  const { t, i18n } = useTranslation('eInvoiceDetails')
  const { language } = i18n

  return (
    // Card
    <div
      key={attachment.Id}
      className={classNames(
        'border-round-2xl border-1 border-100 bg-white p-3 flex align-items-center gap-3 relative',
        styles.card,
        {
          [styles.fullWidth]: fullWidth,
        },
      )}
    >
      {/* Virus Check Status - Top Right */}
      <span className={styles.virusCheckBadge}>
        <VirusCheckStatus virusChecked={attachment.VirusChecked} />
      </span>

      {/* Content container*/}
      <div className={classNames('flex flex-column gap-1', styles.content)}>
        {/* Category */}
        {attachment.DocumentType && (
          <span className="text-xs font-bold text-400">
            {t(
              `stepper.additionalDocuments.table.columns.documentType.options.${attachment.DocumentType}`,
            )}
          </span>
        )}

        {/* Filename */}
        {attachment.FileName && (
          <h4
            className={classNames('m-0 text-sm', styles.truncate)}
            title={attachment.FileName}
          >
            {attachment.FileName}
          </h4>
        )}

        {attachment?.DocServUri && (
          <div className="flex justify-content-end align-items-center gap-2">
            {/* Size */}
            {attachment.FileSizeInBytes && (
              <span className="text-xs text-400 mr-auto">
                {getFormattedFileSize(attachment.FileSizeInBytes, language)}
              </span>
            )}

            {/* Preview */}
            <AttachmentOpenInTabButton
              key={`att-tab-${attachment.Id}`}
              aria-label={t('stepper.additionalDocuments.table.view.label')}
              fullUrl={attachment.DocServUri as string}
              mimeType={attachment.MimeType || ''}
              fileName={attachment.FileName}
              size="small"
              icon="pi pi-eye"
            />

            {/* Download */}
            <AttachmentDownloadButton
              key={`attachment-download-${attachment.Id}`}
              aria-label={t('stepper.additionalDocuments.table.download.label')}
              fullUrl={attachment.DocServUri as string}
              mimeType={attachment.MimeType || ''}
              fileName={attachment.FileName}
              size="small"
              rounded
              icon="pi pi-download"
            />
          </div>
        )}
      </div>
    </div>
  )
}
